/**
 * RecipeListPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  ModalLayout,
  Text,
} from 'components';
import {
  Spin,
  Table,
  Input,
  Radio,
  Button,
  Flex,
  Tag,
  Image,
  Select,
  DatePicker,
} from 'antd';
import RecipeRegisterContainer from '../RecipeRegister';
import dayjs from 'dayjs';
import { STORE_URL } from 'utils';
import { downloadXlsx } from 'utils/XlsxDownloader';

const INITIAL_FILTER_ITEM = {
  is_show: null,
  is_main: null,
};

const RecipeListPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    data = [],
    isLoading = false,
    setIsLoading = () => {},
    selectedList = [],
    onSelect = () => {},
    onSubmit = () => {},
    onSort,
    sortItems,
    isOpened = false,
    onOpen = () => {},
    dateValue,
    setDateValue = () => {},
    setPeriod = () => {},
    INITIAL_VALUE,
    onDelete = () => {},
  } = props;

  /* ===== STATE ===== */
  // 검색값
  const [filterItem, setFilterItem] = React.useState(INITIAL_FILTER_ITEM);

  // 검색 데이터
  const [filterList, setFilterList] = React.useState([]);

  const [modalType, setModalType] = React.useState(false);
  const [modalData, setModalData] = React.useState(null);

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };
  /**
   * 검색
   * --
   */
  const formItem = [
    {
      label: '그룹명',
      form: [
        {
          input: (
            <Input
              value={filterItem['group_product_title']}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  group_product_title: e.target.value,
                }))
              }
              placeholder="그룹명을 입력해주세요"
              style={{ width: 500 }}
              size="large"
            />
          ),
        },
      ],
    },
    {
      label: '노출여부',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem['is_show']}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  is_show: e.target.value,
                }))
              }
            >
              <Radio value={null}>전체</Radio>
              <Radio value={'Y'}>노출</Radio>
              <Radio value={'N'}>숨김</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '메인 노출여부',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem['is_main']}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  is_main: e.target.value,
                }))
              }
            >
              <Radio value={null}>전체</Radio>
              <Radio value={'Y'}>노출</Radio>
              <Radio value={'N'}>미노출</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '등록일',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                value={dateValue.value}
                onChange={(e) =>
                  setDateValue((prev) => ({
                    ...prev,
                    value: e.target.value,
                    start_date: dayjs()
                      .subtract(e.target.value, 'day')
                      .startOf('day'),
                    end_date: dayjs().endOf('day'),
                  }))
                }
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>
              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dateValue.start_date}
                  onChange={(e) =>
                    setDateValue((prev) => ({
                      ...prev,
                      start_date: dayjs(e).startOf('day'),
                    }))
                  }
                  maxDate={dateValue.end_date}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dateValue.end_date}
                  onChange={(e) =>
                    setDateValue((prev) => ({
                      ...prev,
                      end_date: dayjs(e).endOf('day'),
                    }))
                  }
                  minDate={dateValue.start_date}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
  ];

  /* ===== FUNCTION ===== */
  /**
   * 조건 검색
   * --
   */
  const handleSearch = (type) => {
    setIsLoading(true);

    let newList = data;
    switch (type) {
      case 'RESET':
        setFilterItem(INITIAL_FILTER_ITEM);
        break;
      case 'SEARCH':
        const filtered = Object.entries(filterItem).filter(
          ([, value]) => value
        );
        newList = newList.filter((item) =>
          filtered.every(([key, value]) => {
            if (key === 'group_product_title') {
              return item[key]?.includes(value);
            } else {
              return item[key] === value;
            }
          })
        );

        break;
      default:
        break;
    }

    setTimeout(() => {
      setFilterList(newList);
      setIsLoading(false);
    }, 1000);
  };

  /**
   * 아이템 순서 변경 (0-based index 기준)
   * @param {object} child - 순서를 변경할 대상 객체 (예: { present_card_id, order, ... })
   * @param {number} newIndex - 목표 인덱스 (0부터 시작)
   */
  const orderChange = (child, newIndex) => {
    // sortItems는 객체 배열이어야 합니다.
    // 우선 기존 배열의 복사본을 생성합니다.
    const newArr = [...sortItems];
    // 대상 객체의 현재 인덱스를 찾습니다.
    // banner_id나 다른 고유값을 기준으로 찾는 것을 권장합니다.
    const currentIndex = newArr.findIndex(
      (item) => item.group_product_id === child.group_product_id
    );
    if (currentIndex === -1) return; // 대상 객체를 찾지 못하면 종료
    if (currentIndex === newIndex) return; // 인덱스가 동일하면 변경할 필요 없음
    // 현재 인덱스에서 대상 아이템 제거
    const [movedItem] = newArr.splice(currentIndex, 1);
    // 목표 인덱스 위치에 대상 아이템 삽입
    newArr.splice(newIndex, 0, movedItem);

    // 배열 내의 모든 객체에 대해 order 값을 재할당 (1부터 시작하도록)
    const updatedList = newArr.map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    // 최종 updatedList는 객체 배열 형태로 저장됩니다.
    onSort(updatedList);
  };

  /* ===== HOOKS ===== */
  /**
   * 테이블 컬럼
   * --
   */
  const columns = React.useMemo(() => {
    return [
      {
        title: '그룹명',
        key: 'group_product_title',
        dataIndex: 'group_product_title',
        sorter: (a, b) =>
          a.group_product_title.localeCompare(b.group_product_title, 'ko'),
      },
      {
        title: '썸네일',
        dataIndex: 'thumbnail_url',
        key: 'thumbnail_url',
        width: 100,
        render: (thumbnail_url) => (
          <Image
            src={`${STORE_URL}/${thumbnail_url}`}
            width={80}
            height={50}
            preview={false}
          />
        ),
      },
      {
        title: '그룹설명',
        key: 'group_product_description',
        dataIndex: 'group_product_description',
      },
      {
        title: '노출여부',
        key: 'is_show',
        dataIndex: 'is_show',
        render: (item) => (
          <Tag color={item === 'Y' ? 'processing' : 'default'}>
            {item === 'Y' ? '노출' : '숨김'}
          </Tag>
        ),
      },
      {
        title: '메인노출여부',
        key: 'is_main',
        dataIndex: 'is_main',
        render: (item) => (
          <Tag color={item === 'Y' ? 'processing' : 'default'}>
            {item === 'Y' ? '노출' : '숨김'}
          </Tag>
        ),
      },
      {
        title: '순서',
        key: 'order',
        dataIndex: 'order',
        sorter: (a, b) => a.order - b.order,
      },
      {
        title: '생성일',
        key: 'created_at',
        dataIndex: 'created_at',
        render: (item) => dayjs.unix(item).format('YYYY.MM.DD HH:mm:ss'),
        sorter: (a, b) => a.created_at - b.created_at,
      },
      {
        title: '수정일',
        key: 'updated_at',
        dataIndex: 'updated_at',
        render: (item) => dayjs.unix(item).format('YYYY.MM.DD HH:mm:ss'),
        sorter: (a, b) => a.updated_at - b.updated_at,
      },
    ];
  }, []);

  /**
   * 데이터 세팅
   * --
   */
  React.useEffect(() => {
    handleSearch('SEARCH');

    // eslint-disable-next-line
  }, [data]);

  /**
   * 순서정렬 아이템 정제
   * --
   */
  React.useEffect(() => {
    if (!isOpened) return;
    if (modalType !== 'SORT') return;

    const filterItems = [...filterList]?.sort((a, b) => a?.order - b?.order);

    onSort(filterItems);

    // eslint-disable-next-line
  }, [filterList, modalType, isOpened]);
  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row gutter={[16, 16]}>
            <Col x={24}>
              <Title style={{ ...styles.title }}>레시피 상품 목록</Title>
            </Col>

            {/* SECTION: 검색 */}
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0} backgroundColor={'none'}>
                <ExpandableContainer borderRadius={4} padding={20}>
                  {formItem.map((_item, _index) => (
                    <div
                      key={`form_${_index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 0px',
                      }}
                    >
                      <div
                        style={{ width: 120, fontSize: 13, fontWeight: 400 }}
                      >
                        <label>{_item.label}</label>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {_item.form.map((_subitem, _subkey) => (
                          <React.Fragment key={`input_${_subkey}`}>
                            {_subitem.input}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </ExpandableContainer>

                {/* 검색/초기화 버튼 */}
                <Flex
                  gap={12}
                  justify="center"
                  align="center"
                  style={{ marginTop: 20 }}
                >
                  <Button
                    size="large"
                    type="primary"
                    onClick={() => {
                      setPeriod({
                        start_date: dateValue.start_date.unix(),
                        end_date: dateValue.end_date.unix(),
                      });
                      handleSearch('SEARCH');
                    }}
                  >
                    검색
                  </Button>
                  <Button
                    size="large"
                    onClick={() => {
                      setPeriod({
                        start_date: INITIAL_VALUE.start_date.unix(),
                        end_date: INITIAL_VALUE.end_date.unix(),
                      });
                      setDateValue(INITIAL_VALUE);
                      handleSearch('RESET');
                    }}
                  >
                    초기화
                  </Button>
                </Flex>
                {/* 검색/초기화 버튼 끝 */}
              </Content>
            </Col>
            {/* !SECTION: 검색 */}

            {/* SECTION: 상품목록 */}

            <Col x={24}>
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <Title
                  size={5}
                  subButton={
                    <div>
                      <Flex gap={5}>
                        <Button
                          danger
                          disabled={!selectedList?.length}
                          onClick={() => onDelete()}
                        >
                          {selectedList?.length}건 삭제
                        </Button>
                        <Button
                          disabled={!selectedList?.length}
                          onClick={() => onSubmit('is_show', 'Y')}
                        >
                          {selectedList?.length}건 노출
                        </Button>
                        <Button
                          disabled={!selectedList?.length}
                          onClick={() => onSubmit('is_show', 'N')}
                        >
                          {selectedList?.length}건 노출 안함
                        </Button>
                        <Button
                          disabled={!selectedList?.length}
                          onClick={() => onSubmit('is_main', 'Y')}
                        >
                          {selectedList?.length}건 메인 진열
                        </Button>
                        <Button
                          disabled={!selectedList?.length}
                          onClick={() => onSubmit('is_main', 'N')}
                        >
                          {selectedList?.length}건 메인 진열 안함
                        </Button>
                        <Button
                          onClick={() => {
                            setModalType('SORT');
                            onOpen(true);
                          }}
                        >
                          순서 변경
                        </Button>
                        <Button
                          style={{ marginRight: 5 }}
                          onClick={() =>
                            downloadXlsx({
                              columns: columns,
                              rows: filterList,
                              fileName: '레시피',
                            })
                          }
                        >
                          엑셀로 다운로드
                        </Button>
                      </Flex>
                    </div>
                  }
                >
                  <span>
                    검색된 레시피상품 총 <span>{filterList?.length}</span>개
                  </span>
                </Title>
                <br />
                <Table
                  rowKey={'group_product_id'}
                  columns={columns}
                  dataSource={filterList}
                  scroll={{
                    x: 'max-content',
                  }}
                  rowSelection={{
                    onChange: (_, rows) => onSelect(rows),
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      setModalType('UPDATE');
                      onOpen(true);
                      setModalData({ ...record, key: dayjs().unix() });
                    },
                  })}
                />
              </Content>
            </Col>

            {/* SECTION: 상품목록 */}
          </Row>
          {/* 수정 모달 */}
          <ModalLayout
            type="modal"
            centered
            title={'그룹상품 수정'}
            open={isOpened}
            onCancel={() => {
              onOpen(false);
              setModalData(null);
              onSort([]);
            }}
            closable
            width={modalType === 'UPDATE' ? '90%' : '30%'}
            footer={
              <Content
                maxWidth={'100%'}
                padding={0}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                {modalType === 'UPDATE' ? (
                  <RecipeRegisterContainer
                    data={modalData}
                    modalType={modalType}
                    onOpen={onOpen}
                    isOpened={isOpened}
                  />
                ) : (
                  <Flex vertical gap={24} style={{ padding: '12px 0px' }}>
                    {sortItems?.map((_item, _index) => (
                      <Flex
                        key={`group-${_item.group_product_id}`}
                        justify="space-between"
                        align="center"
                        style={{ width: '500px' }}
                      >
                        <Flex align="center" gap={12}>
                          <div
                            style={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #BDBDBD',
                            }}
                          >
                            <Image
                              src={`${STORE_URL}/${_item?.thumbnail_url}`}
                              width={80}
                              height={'100%'}
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                          <Text style={{ flex: 1 }}>
                            {_item?.group_product_title}
                          </Text>
                        </Flex>

                        <Select
                          value={_index}
                          options={new Array(sortItems?.length)
                            .fill(null)
                            .map((_, index) => ({
                              label: `${index + 1}번`,
                              value: index,
                            }))}
                          onChange={(e) => orderChange(_item, Number(e))}
                        />
                      </Flex>
                    ))}
                    <Flex align="center" justify="center" gap={10}>
                      <Button
                        type="primary"
                        size="large"
                        onClick={() => onSubmit('sort', 'Y')}
                      >
                        저장
                      </Button>
                      <Button
                        type="primary"
                        size="large"
                        danger
                        onClick={() => {
                          onOpen(false);
                          onSort([]);
                        }}
                      >
                        취소
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </Content>
            }
          >
            <Content maxWidth={'100%'} padding={'30px 0px'}></Content>
          </ModalLayout>

          {/* 수정 모달 */}
        </Content>
      </Spin>
    </>
  );
};

export default RecipeListPresenter;
